import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { obtenerControlRobot } from '../services/seguimientoExpedientesService';
import { usuarioService } from '../services/usuarioService';
import { useAuth } from '../auth/AuthContext';
import PageHeader from '../components/ui/PageHeader';

// Devuelve color/etiqueta del estado para el badge.
export const estiloEstado = (exp) => {
    if (exp.estadoGeneral === 'EN_CURSO') {
        return { texto: exp.estadoLabel || 'En curso', fg: '#9A5B00', bg: '#FBF1E0' };
    }
    switch (exp.resultado) {
        case 'FAVORABLE':
            return { texto: exp.estadoLabel || 'Favorable', fg: '#0F6E56', bg: '#E1F5EE' };
        case 'DESFAVORABLE':
            return { texto: exp.estadoLabel || 'Desfavorable', fg: '#993556', bg: '#FBEAF0' };
        case 'OTROS':
            return { texto: exp.estadoLabel || 'Otros', fg: '#5F5E5A', bg: '#F1EFE8' };
        default:
            return { texto: exp.estadoLabel || 'Cerrado', fg: '#444441', bg: '#F1EFE8' };
    }
};

export const Badge = ({ exp }) => {
    const e = estiloEstado(exp);
    return (
        <span
            className="badge"
            style={{ background: e.bg, color: e.fg, padding: '0.5rem 0.9rem', borderRadius: 6, fontWeight: 600 }}
        >
            {e.texto}
        </span>
    );
};

const ModalEmailNotificaciones = ({ open, usuario, onClose }) => {
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [emailLogin, setEmailLogin] = useState('');
    const [valor, setValor] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setCargando(true);
        setError(null);
        usuarioService.getMe()
            .then(({ data }) => {
                setEmailLogin(data?.email || '');
                setValor(data?.emailNotificaciones || '');
            })
            .catch(() => setError('No se pudo cargar tu configuración actual.'))
            .finally(() => setCargando(false));
    }, [open]);

    if (!open) return null;

    const guardar = async (nuevoValor) => {
        setGuardando(true);
        setError(null);
        try {
            await usuarioService.updateProfile(usuario.id, { emailNotificaciones: nuevoValor });
            setValor(nuevoValor);
            onClose();
        } catch (e) {
            setError('No se pudo guardar. Probá de nuevo.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={onClose}
        >
            <div
                style={{ background: '#fff', borderRadius: 16, width: 440, maxWidth: '95vw', boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '20px 28px', background: 'var(--primary-text)', color: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <i className="bi bi-envelope-at" style={{ fontSize: '1.4rem' }} />
                        <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1.05rem' }}>Email de notificaciones</h5>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#fff', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                        <i className="bi bi-x-lg" />
                    </button>
                </div>
                <div style={{ padding: '24px 28px' }}>
                    {cargando ? (
                        <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary" /></div>
                    ) : (
                        <>
                            <p className="text-muted" style={{ fontSize: 13 }}>
                                Por defecto las notificaciones de ANSES te llegan a <strong>{emailLogin}</strong> (el mismo
                                que usás para entrar al sistema). Si querés recibirlas en otra casilla, poné una acá.
                            </p>
                            <label className="form-label" >
                                Email alternativo (opcional)
                            </label>
                            <input
                                type="email"
                                className="form-control"
                                placeholder={emailLogin}
                                value={valor}
                                onChange={(e) => setValor(e.target.value)}
                                style={{ borderRadius: 8 }}
                            />
                            {error && <div className="alert alert-danger mt-3 mb-0 py-2" style={{ fontSize: 13 }}>{error}</div>}
                        </>
                    )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '16px 28px', borderTop: '1px solid #e2e8f0' }}>
                    <button
                        type="button"
                        className="btn btn-link text-danger"
                        disabled={cargando || guardando || !valor}
                        onClick={() => guardar('')}
                        style={{ fontSize: 13, textDecoration: 'none' }}
                    >
                        Usar el de login
                    </button>
                    <div className="d-flex gap-2">
                        <button type="button" className="btn btn-light" onClick={onClose} disabled={guardando}>
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn-ver-calendario-home"
                            style={{ background: 'var(--primary-text)' }}
                            onClick={() => guardar(valor)}
                            disabled={cargando || guardando}
                        >
                            {guardando ? 'Guardando…' : 'Guardar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const formatoFechaHora = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
}) : '—');

const formatoDuracion = (inicio, fin) => {
    if (!inicio || !fin) return '—';
    const seg = Math.round((new Date(fin) - new Date(inicio)) / 1000);
    return seg < 60 ? `${seg} s` : `${Math.floor(seg / 60)} min ${seg % 60} s`;
};

const COLOR_ESTADO_CORRIDA = { OK: '#166534', ERROR: '#b91c1c' };

function Tarjeta({ titulo, valor, detalle, color = '#1e293b', alerta = false }) {
    return (
        <div style={{
            background: alerta ? '#fef2f2' : '#ffffff', border: `1px solid ${alerta ? '#fecaca' : '#e2e8f0'}`,
            borderRadius: 10, padding: '0.8rem 1rem', minWidth: 150, flex: '1 1 150px',
        }}>
            <div style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{titulo}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{valor}</div>
            {detalle && <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{detalle}</div>}
        </div>
    );
}

/**
 * Seguimiento de expedientes = la página de CONTROL del robot de ANSES (decisión del usuario,
 * 26-oct-2026). El seguimiento por cliente se maneja desde Para aprobar (prioridad, avisos, revisión
 * manual); acá se ve si el robot anda, por qué no entra cada grupo de clientes —y si el problema es
 * del robot o de los clientes— y qué hizo en las últimas corridas.
 */
const SeguimientoExpedientesPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [control, setControl] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);
    const [modalEmailAbierto, setModalEmailAbierto] = useState(false);
    const [soloErrores, setSoloErrores] = useState(false);

    const cargar = useCallback(async () => {
        setCargando(true);
        setError(null);
        try {
            setControl(await obtenerControlRobot());
        } catch (e) {
            setError('No se pudo cargar el estado del robot.');
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => {
        cargar();
    }, [cargar]);

    const corridas = useMemo(() => (control?.corridas || [])
        .filter((c) => !soloErrores || c.estado !== 'OK'), [control, soloErrores]);

    const c = control?.conexiones;
    const fallasDelRobot = (control?.fallas || []).filter((f) => f.esDelRobot);

    return (
        <div className="area-anses container-fluid mt-4">
            <PageHeader
                variante="plano"
                icono="cpu"
                titulo="Control del robot de ANSES"
                descripcion="Si el robot está funcionando, por qué no entra cada grupo de clientes y qué hizo en las últimas revisiones. El seguimiento de cada trámite está en Para aprobar."
                accion={
                    <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setModalEmailAbierto(true)}
                                title="A qué dirección de mail te llegan a vos los avisos de ANSES">
                            <i className="bi bi-envelope-at me-1" />Email de avisos
                        </button>
                        <button className="btn btn-sm btn-outline-primary" onClick={cargar} disabled={cargando}>
                            <i className="bi bi-arrow-clockwise me-1" />Actualizar
                        </button>
                    </div>
                }
            />

            {error && <div className="alert alert-danger">{error}</div>}
            {cargando && !control && <div className="text-muted">Cargando…</div>}

            {control && !control.disponible && (
                <div className="alert alert-danger"><i className="bi bi-x-octagon me-2" />{control.mensaje}</div>
            )}

            {control?.disponible && (
                <>
                    {fallasDelRobot.length > 0 && (
                        <div className="alert alert-danger">
                            <i className="bi bi-exclamation-octagon-fill me-2" />
                            <strong>Hay un problema del robot, no de los clientes:</strong>{' '}
                            {fallasDelRobot.map((f) => `${f.etiqueta} (${f.cantidad})`).join(' · ')}.
                            Mientras no se arregle, esos clientes no se revisan.
                        </div>
                    )}

                    {/* Estado general */}
                    <div className="d-flex flex-wrap gap-2 mb-4">
                        <Tarjeta titulo="Última revisión buena" valor={formatoFechaHora(control.ultimaRevisionOk)}
                                 alerta={!control.ultimaRevisionOk} color="#0f172a" />
                        <Tarjeta titulo="Revisiones hoy" valor={control.revisionesHoy}
                                 detalle={`Ayer: ${control.revisionesAyer} · Novedades hoy: ${control.novedadesHoy}`} />
                        <Tarjeta titulo="Clientes conectados" valor={c.activas} detalle={`de ${c.total} en el robot`} color="#166534" />
                        <Tarjeta titulo="Falla del robot" valor={c.fallaDelRobot} alerta={c.fallaDelRobot > 0} color="#b91c1c"
                                 detalle="No se revisan hasta arreglar el robot" />
                        <Tarjeta titulo="Revisión manual" valor={c.revisionManual} color="#9a3412"
                                 detalle="Clave rechazada u otros: ver Para aprobar" />
                        <Tarjeta titulo="Esperando su turno" valor={c.vencidas} detalle="Se vuelven a revisar solos" />
                        <Tarjeta titulo="Pausados" valor={c.pausadas} detalle="Clientes terminados" />
                        <Tarjeta titulo="Leen PNC" valor={c.conPnc} />
                    </div>

                    {/* Fallas agrupadas */}
                    <h6 className="fw-bold mb-2"><i className="bi bi-exclamation-triangle me-2" />Por qué no entran</h6>
                    {control.fallas.length === 0 ? (
                        <p className="text-muted">Todos los clientes están entrando.</p>
                    ) : (
                        <div className="table-responsive mb-4">
                            <table className="table table-sm align-middle" style={{ fontSize: '0.85rem' }}>
                                <thead>
                                    <tr style={{ color: '#475569' }}>
                                        <th>Motivo</th><th>De quién</th><th className="text-center">Clientes</th><th>Desde</th><th>Hasta</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {control.fallas.map((f) => (
                                        <tr key={f.motivo} style={f.esDelRobot ? { background: '#fef2f2' } : undefined}>
                                            <td style={{ fontWeight: 600 }}>{f.etiqueta}</td>
                                            <td>{f.esDelRobot
                                                ? <span className="badge bg-danger-subtle text-danger-emphasis">Del robot</span>
                                                : <span className="badge bg-secondary-subtle text-secondary-emphasis">Del cliente / ANSES</span>}</td>
                                            <td className="text-center fw-bold">{f.cantidad}</td>
                                            <td>{formatoFechaHora(f.desde)}</td>
                                            <td>{formatoFechaHora(f.hasta)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Registro de corridas */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="fw-bold mb-0"><i className="bi bi-journal-text me-2" />Últimas revisiones</h6>
                        <label className="form-check-label small">
                            <input type="checkbox" className="form-check-input me-1" checked={soloErrores}
                                   onChange={(e) => setSoloErrores(e.target.checked)} />
                            Solo las que fallaron
                        </label>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-sm table-hover align-middle" style={{ fontSize: '0.82rem' }}>
                            <thead>
                                <tr style={{ color: '#475569' }}>
                                    <th>Inicio</th><th>Cliente</th><th>Resultado</th><th className="text-center">Expedientes</th>
                                    <th className="text-center">Novedades</th><th>Duración</th><th>Error</th>
                                </tr>
                            </thead>
                            <tbody>
                                {corridas.length === 0 && (
                                    <tr><td colSpan={7} className="text-muted">Sin revisiones para mostrar.</td></tr>
                                )}
                                {corridas.map((r, i) => (
                                    <tr key={`${r.cuil}-${r.inicio}-${i}`} style={{ cursor: r.cuil ? 'pointer' : undefined }}
                                        onClick={() => r.cuil && navigate(`/herramientas/seguimiento-expedientes/cliente/${r.cuil}`)}>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatoFechaHora(r.inicio)}</td>
                                        <td>{r.cliente || r.cuil || '—'}</td>
                                        <td style={{ color: COLOR_ESTADO_CORRIDA[r.estado] || '#334155', fontWeight: 600 }}>{r.estado || '—'}</td>
                                        <td className="text-center">{r.expedientes ?? '—'}</td>
                                        <td className="text-center">{r.novedades ? <strong>{r.novedades}</strong> : r.novedades ?? '—'}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatoDuracion(r.inicio, r.fin)}</td>
                                        <td style={{ maxWidth: 320, color: '#b91c1c' }}>{r.error || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <ModalEmailNotificaciones open={modalEmailAbierto} usuario={user} onClose={() => setModalEmailAbierto(false)} />
        </div>
    );
};

export default SeguimientoExpedientesPage;
